/**
 * Error Tracking Utilities
 * Pure functional error tracking for PostHog
 * Following functional programming principles - no mutations, composable
 */

import posthog from './posthog';

/**
 * Pure function to create error context object
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 * @returns {Object} Error context for tracking
 */
export const createErrorContext = (error, context = {}) => ({
  errorName: error.name,
  errorMessage: error.message,
  errorStack: error.stack,
  ...context,
  timestamp: new Date().toISOString()
});

/**
 * Pure function to track error to PostHog
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const trackError = (error, context = {}) => {
  if (typeof window !== 'undefined' && posthog) {
    const errorContext = createErrorContext(error, context);
    posthog.captureException(error, errorContext);
  }
};

/**
 * Higher-order function to wrap API calls with error tracking
 * Functional wrapper - doesn't mutate, returns new function
 * @param {Function} apiCall - API function to wrap
 * @param {Object} metadata - Static metadata about the API call
 * @returns {Function} Wrapped API function with error tracking
 */
export const withErrorTracking = (apiCall, metadata = {}) => {
  return async (...args) => {
    try {
      return await apiCall(...args);
    } catch (error) {
      // Track error with context
      trackError(error, {
        type: 'api_error',
        ...metadata,
        // Add runtime context
        args: JSON.stringify(args).substring(0, 200) // Limit size
      });

      // Re-throw to preserve error flow
      throw error;
    }
  };
};

/**
 * Pure function to create API error context
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @param {Error} error - Error object
 * @returns {Object} API error context
 */
export const createApiErrorContext = (endpoint, options = {}, error = {}) => ({
  type: 'api_error',
  endpoint,
  method: options.method || 'GET',
  status: error.status,
  statusText: error.statusText || error.message,
  hasAuth: !!options.headers?.Authorization
});

/**
 * Track specific API error with full context
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @param {Error} error - Error object
 */
export const trackApiError = (endpoint, options, error) => {
  const context = createApiErrorContext(endpoint, options, error);
  trackError(error, context);
};
